import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

/**
 * Formats a time range string for display in the UI.
 *
 * This function takes a time range string in the format "HH:MM-HH:MM" and
 * formats it with proper spacing and separator for user-friendly display.
 *
 * @param {string} time - Time range string in format "HH:MM-HH:MM" (e.g., "06:00-10:00")
 *
 * @returns {string} Formatted time range string with spaces around the separator (e.g., "06:00 - 10:00")
 *
 * @example
 * // Format a time range
 * const formatted = formatTime('06:00-10:00');
 * console.log(formatted); // '06:00 - 10:00'
 *
 * @example
 * // Format overnight time range
 * const overnight = formatTime('22:00-06:00');
 * console.log(overnight); // '22:00 - 06:00'
 *
 */
export const formatTime = (time: string): string => {
	const [startTime, endTime] = time.split('-');
	return `${startTime} - ${endTime}`;
};

/**
 * Formats an array of day abbreviations into a localized, comma-separated string.
 *
 * This function takes an array of day abbreviations (Mon, Tue, etc.) and converts
 * them to localized full day names using the application's internationalization
 * system. It falls back to English defaults if translations are not available.
 *
 * @param {string[]} days - Array of day abbreviations ('Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun')
 *
 * @returns {string} Comma-separated string of localized day names
 *
 * @example
 * // Format weekdays
 * const weekdays = formatDays(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
 * console.log(weekdays); // 'Monday, Tuesday, Wednesday, Thursday, Friday'
 *
 * @example
 * // Format weekend days
 * const weekend = formatDays(['Sat', 'Sun']);
 * console.log(weekend); // 'Saturday, Sunday'
 *
 * @example
 * // Format all days
 * const allDays = formatDays(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
 * console.log(allDays); // 'Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday'
 *
 */
export const formatDays = (days: string[]): string => {
	const $format = get(format);
	const dayKeyMap: Record<string, string> = {
		Mon: 'option_monday',
		Tue: 'option_tuesday',
		Wed: 'option_wednesday',
		Thu: 'option_thursday',
		Fri: 'option_friday',
		Sat: 'option_saturday',
		Sun: 'option_sunday',
	};
	const dayDefaultMap: Record<string, string> = {
		Mon: 'Monday',
		Tue: 'Tuesday',
		Wed: 'Wednesday',
		Thu: 'Thursday',
		Fri: 'Friday',
		Sat: 'Saturday',
		Sun: 'Sunday',
	};
	return days
		.map((day) => $format(dayKeyMap[day] || '', { default: dayDefaultMap[day] || day }))
		.join(', ');
};
